/**
 * Shift-click range selection for a multi-select list (2026-08-02) — the file-manager
 * convention, as one pure function: a plain toggle flips the clicked key, a shift-toggle applies
 * the clicked key's *new* state to everything between it and the previously toggled key. Applying
 * the clicked key's state (rather than always selecting) is what makes shift work in both
 * directions — checking with shift ranges a check, unchecking with shift ranges an uncheck.
 *
 * `order` is the **display** order of the rows the reader is looking at — filtered, sorted,
 * reversed, whatever the screen currently shows — because "between" is a claim about the screen,
 * not about the data. The caller keeps the anchor (the last key it passed here) and hands it back;
 * an anchor that is no longer in `order` (its row was filtered away or deleted) degrades to a
 * plain toggle rather than guessing at a range nobody can see.
 */
export function rangeToggle(
  current: ReadonlySet<string>,
  order: readonly string[],
  anchor: string | null,
  key: string,
  range: boolean,
): ReadonlySet<string> {
  const next = new Set(current);
  const target = !current.has(key);

  const from = anchor === null ? -1 : order.indexOf(anchor);
  const to = order.indexOf(key);
  if (!range || anchor === key || from === -1 || to === -1) {
    if (target) next.add(key);
    else next.delete(key);
    return next;
  }

  const [lo, hi] = from < to ? [from, to] : [to, from];
  for (const inRange of order.slice(lo, hi + 1)) {
    if (target) next.add(inRange);
    else next.delete(inRange);
  }
  return next;
}
